import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import type { ProjectDetail } from '@bits/shared/zod';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type Values = { name: string; clientId: string; costCenterId: string; managerId: string };

export function ProjectEditDialog({
  project,
  open,
  onOpenChange,
}: {
  project: ProjectDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const isDelivery = project.type === 'client_delivery';

  const clientsQuery = useQuery({
    queryKey: ['clients-for-projects'],
    queryFn: () => api.listClients({ status: 'active', page: 1, pageSize: 100 }),
    enabled: open && isDelivery,
  });
  const costCentersQuery = useQuery({
    queryKey: ['cost-centers'],
    queryFn: () => api.listCostCenters(),
    enabled: open,
  });
  const peopleQuery = useQuery({
    queryKey: ['people-simple'],
    queryFn: () => api.listPeopleSimple(),
    enabled: open,
  });

  const form = useForm<Values>();
  React.useEffect(() => {
    if (!open) return;
    form.reset({
      name: project.name,
      clientId: project.clientId ?? '',
      costCenterId: project.costCenterId,
      managerId: project.managerId ?? '',
    });
    setErrorMessage(null);
  }, [open, project, form]);

  const mutation = useMutation({
    mutationFn: (v: Values) => {
      const body: Parameters<typeof api.updateProject>[1] = {};
      if (v.name !== project.name) body.name = v.name;
      if (isDelivery && v.clientId && v.clientId !== project.clientId) body.clientId = v.clientId;
      if (v.costCenterId !== project.costCenterId) body.costCenterId = v.costCenterId;
      if ((v.managerId || null) !== project.managerId) body.managerId = v.managerId || null;
      return api.updateProject(project.id, body);
    },
    onSuccess: async () => {
      onOpenChange(false);
      await queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit project</DialogTitle>
          <DialogDescription>
            The code <span className="font-mono">{project.code}</span> is fixed — documents and
            reports reference it.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} className="flex flex-col gap-3">
          <Field label="Name" required error={form.formState.errors.name?.message}>
            <Input {...form.register('name', { required: 'Enter a name' })} />
          </Field>
          {isDelivery ? (
            <Field label="Client" required error={form.formState.errors.clientId?.message}>
              <Select {...form.register('clientId', { required: 'Pick a client' })}>
                <option value="">Select…</option>
                {(clientsQuery.data?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          <Field label="Cost center" required error={form.formState.errors.costCenterId?.message}>
            <Select {...form.register('costCenterId', { required: 'Pick a cost center' })}>
              <option value="">Select…</option>
              {(costCentersQuery.data ?? [])
                .filter((c) => c.active || c.id === project.costCenterId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.isDefaultOverhead ? ' (default)' : ''}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="Project manager" hint="Routes task allocation and work-log approvals.">
            <Select {...form.register('managerId')}>
              <option value="">— none —</option>
              {(peopleQuery.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
          {errorMessage ? (
            <Alert variant="destructive">
              <AlertTitle>Save failed</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
